import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level React error boundary.
 *
 * Catches any unhandled render/lifecycle error below it and shows a quiet
 * fallback card with a "Reload" button. We deliberately do NOT use a sonner
 * toast here — the toast layer itself may be in the broken subtree when the
 * boundary fires. The on-screen card is the sole error surface.
 *
 * Logging: `console.error` keeps the error visible in dev-tools without
 * surfacing it to end-users.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex h-full min-h-screen items-center justify-center bg-background p-6">
          <div className="flex max-w-sm flex-col items-center gap-4 rounded-xl border border-border/40 bg-card p-8 text-center shadow-neu-raised-lg">
            <p className="text-base font-semibold text-foreground">
              Something went wrong
            </p>
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred. Reload the page to continue.
            </p>
            <button
              type="button"
              onClick={() => { window.location.reload(); }}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
